import React from 'react';
import { 
  Modal, 
  Descriptions, 
  Tag, 
  Space, 
  Typography, 
  Row,
  Col,
  Card,
  Button,
  Statistic
} from 'antd';
import { 
  EnvironmentOutlined,
  PhoneOutlined,
  UserOutlined,
  InboxOutlined,
  EditOutlined,
  CalendarOutlined,
  IdcardOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { WarehouseWithStats } from '../../store/api/warehousesApi';
// dayjs was previously imported but is not used in this modal

const { Title, Text } = Typography;

interface WarehouseViewModalProps {
  visible: boolean;
  warehouse: WarehouseWithStats | null;
  onClose: () => void;
  onEdit?: () => void;
  warehouseStats?: {
    totalCargo: number;
    totalWeight: number;
    totalVolume: number;
    longTermStorage: number;
  } | null;
}

const WarehouseViewModal: React.FC<WarehouseViewModalProps> = ({
  visible,
  warehouse,
  onClose,
  onEdit,
  warehouseStats
}) => {
  if (!warehouse) return null;

  return (
    <Modal
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>
            Информация о складе: {warehouse.name}
          </Title>
          <Tag color="blue">ID: {warehouse.id}</Tag>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={
        <Space>
          <Button onClick={onClose}>
            Закрыть
          </Button>
          {onEdit && (
            <Button type="primary" icon={<EditOutlined />} onClick={onEdit}>
              Редактировать
            </Button>
          )}
        </Space>
      }
      width={800}
      styles={{ body: { padding: '24px' } }}
    >
      <Row gutter={[16, 16]}>
        {/* Основная информация о складе */}
        <Col span={24}>
          <Card title="Основная информация" size="small">
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Название склада">
                <Text strong>{warehouse.name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Город">
                <Space>
                  <EnvironmentOutlined />
                  <Text strong>{warehouse.city}</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Полный адрес" span={2}>
                {warehouse.address ? (
                  <Space>
                    <EnvironmentOutlined />
                    <Text>{warehouse.address}</Text>
                  </Space>
                ) : (
                  <Text type="secondary">Не указан</Text>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* Контактная информация */}
        <Col span={24}>
          <Card title="Контактная информация" size="small">
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Контактное лицо">
                {warehouse.contactPerson ? (
                  <Space>
                    <UserOutlined />
                    <Text>{warehouse.contactPerson}</Text>
                  </Space>
                ) : (
                  <Text type="secondary">Не указано</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Телефон">
                {warehouse.phone ? (
                  <Space>
                    <PhoneOutlined />
                    <Text copyable>{warehouse.phone}</Text>
                  </Space>
                ) : (
                  <Text type="secondary">Не указан</Text>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* Статистика по грузам */}
        <Col span={24}>
          <Card title="Статистика по грузам" size="small">
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="Количество грузов"
                  value={warehouseStats?.totalCargo ?? warehouse._count?.cargoOnWarehouse ?? 0}
                  prefix={<InboxOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Общий вес"
                  value={warehouseStats?.totalWeight ?? 0}
                  suffix="кг"
                  precision={0}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Общий объем"
                  value={warehouseStats?.totalVolume ?? 0}
                  suffix="м³"
                  precision={2}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Долго хранящиеся"
                  value={warehouseStats?.longTermStorage ?? 0}
                  prefix={<WarningOutlined />}
                  valueStyle={{ color: (warehouseStats?.longTermStorage ?? 0) > 0 ? '#ff4d4f' : '#3f8600' }}
                />
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Дополнительная информация */}
        <Col span={24}>
          <Card title="Дополнительная информация" size="small">
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Дата создания">
                <Space>
                  <CalendarOutlined />
                  <Text type="secondary">Не указана</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Последнее обновление">
                <Space>
                  <CalendarOutlined />
                  <Text type="secondary">Не указана</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Идентификатор склада">
                <Space>
                  <IdcardOutlined />
                  <Text code>#{warehouse.id}</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Статус">
                <Tag color="green">Активный</Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>
    </Modal>
  );
};

export default WarehouseViewModal; 